import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import bytes from 'bytes'
import pickBy from 'lodash/pickBy'
import ceil from 'lodash/ceil'

import {
  FAILED,
  UPLOADED,
  ImageMetadata
} from '../../utils/upload'

import Echo from '../../utils/echo'
import UploadedImage from './UploadedImage'
import { OrderFormContext } from '../../contexts'
import { Tooltip } from 'antd'

const UploadSession = ({
  session,
  updateCallback = () => {},
  original,
  preview,
  thumbnail,
  croppedArea,
  onClickRemove = () => {}
}) => {
  // Get context
  const orderFormContext = React.useContext(OrderFormContext)

  /**
   * Cropped area aspect
   * @type {number}
   */
  const croppedAreaAspect = React.useMemo(() => {
    if (orderFormContext.frameSize) {
      return Number(orderFormContext.frameSize.frame_height) / Number(orderFormContext.frameSize.frame_width)
    } else {
      return null
    }
  }, [orderFormContext.frameSize])

  /**
   * Original area aspect
   * @type {number}
   */
  const originalAreaAspect = React.useMemo(() => {
    return Number(original.props.image_sizes[0]) / Number(original.props.image_sizes[1])
  }, [original.props.image_sizes])

  /**
   * Represents the dynamically calculated height of the current cropped image area
   * based on the original image's dimensions and the cropping percentage.
   */
  const currentHeight = React.useMemo(
    () => ceil(original.props.image_sizes[1] * ((croppedArea?.height || 100) / 100), 3),
    [original.props.image_sizes, croppedArea?.height])

  /**
   * Memoized value representing the current width of the image based on its original size
   * and the cropped area's width percentage. Calculated using the first element of
   * the original `image_sizes` array and the width ratio of the cropped area.
   */
  const currentWidth = React.useMemo(
    () => ceil(original.props.image_sizes[0] * ((croppedArea?.width || 100) / 100), 3),
    [original.props.image_sizes, croppedArea?.width])

  useEffect(() => {
    Echo.channel(`uploadSession.${session}`)
      .listen('ImageUploaded', onImageUploaded)
      .listen('FailedProcessImage', onProcessFailed)
      .listen('UploadSessionDestroyed', onDestroyed)

    return () => Echo.leave(`uploadSession.${session}`)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onImageUploaded = ({ uploadSession }) => {
    const data = (uploadSession && uploadSession.data) || {}

    updateCallback(session, pickBy({
      status: UPLOADED,
      original: new ImageMetadata(data.original),
    }))

    onImageUploaded && onImageUploaded(session)
  }

  const onProcessFailed = () => {
    updateCallback(session, { status: FAILED })

    onImageUploaded && onImageUploaded(session)
  }

  const onDestroyed = () => {
    onDestroyed && onDestroyed(session)
  }

  const file = original || preview

  return (
    <div className="UploadSession isValid">
      <div className="UploadSession__Preview">
        <UploadedImage
          noCroppingApplied /** Set to false if you want to apply cropping to the image */
          session={session}
          original={original}
          preview={preview}
          thumbnail={thumbnail}
          croppedArea={croppedArea}
          originalAreaAspect={originalAreaAspect}
          croppedAreaAspect={croppedAreaAspect}
        />

        <div className="UploadSession__Actions">
          <button
            type="button"
            className="UploadSession__Remove"
            onClick={() => onClickRemove(session)}>&times;
          </button>
        </div>

        {file && <div className="UploadSession__Overlay p-2 flex gap-2 items-center justify-between text-xs">
          <p>{bytes(file.getFileSize())}</p>
          <p>{currentWidth} x {currentHeight}px</p>
        </div>}
      </div>

      <div className="UploadSession__Meta">
        <Tooltip placement="bottom" title={file?.getFileName() || null}>
          <h2 className='max-w-full truncate'>{file ? file.getFileName() : '...'}</h2>
        </Tooltip>
      </div>
    </div>
  )
}

UploadSession.propTypes = {
  session: PropTypes.string.isRequired,
  status: PropTypes.string.isRequired,
  frameSize: PropTypes.object,
  updateCallback: PropTypes.func.isRequired,

  aspect: PropTypes.string,
  orientation: PropTypes.string,
  original: PropTypes.instanceOf(ImageMetadata),
  preview: PropTypes.instanceOf(ImageMetadata),
  thumbnail: PropTypes.instanceOf(ImageMetadata),

  onClickCrop: PropTypes.func,
  onClickRotate: PropTypes.func,
  onClickRemove: PropTypes.func,

  onImageCropped: PropTypes.func,
  onImageResized: PropTypes.func,
  onImageUploaded: PropTypes.func,
  onProcessFailed: PropTypes.func,
  onDestroyed: PropTypes.func,

  croppedArea: PropTypes.object, // croppedArea is optional
}

export default UploadSession
